import { useState } from "react";
import {
  Box,
  Flex,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Tag,
  TagLabel,
  Text,
  Tooltip,
  VStack,
  useBreakpointValue,
} from "@chakra-ui/react";
import { CheckIcon, CloseIcon, LockIcon } from "@chakra-ui/icons";
import { MdListAlt, MdSearch } from "react-icons/md";
import {
  CatalogoDisparo,
  ContextoDisparo,
  PropriedadeCatalogo,
  rotuloContexto,
} from "@/models/consignado/disparo.model";
import {
  GrupoEntidade,
  entidadeDoContexto,
  gruposDoContexto,
  propriedadesDeTemplate,
  propriedadesFiltraveis,
} from "./catalogo.helpers";
import PropriedadeList from "./PropriedadeList";
import { brandOutline, brandSolid, cardStyles, fieldFont } from "./styleTokens";

type Props = {
  contexto: ContextoDisparo;
  catalogo?: CatalogoDisparo;
  // Qual "sessão" do formulário está ativa: com "Identificação do disparo"
  // aberta é a sessão do disparo (campo de filtro); fechada (foco foi pros
  // canais) é a sessão da mensagem (variáveis). Nunca as duas juntas.
  disparoAberto: boolean;
  /** Gatilho sem filtro (evento): a identificação aberta não tem "+" de filtro. */
  semFiltro?: boolean;
  // Campo de um filtro — só aparece enquanto existir uma pill "ativa" no
  // formulário (ver FiltroActiveContext); some assim que o campo é escolhido.
  hasActiveFiltroTarget?: boolean;
  onSelectFiltroCampo?: (propriedade: PropriedadeCatalogo) => void;
  // Campo de mensagem em foco (ver VariavelInsertContext).
  hasActiveTarget: boolean;
  onSelectVariavel: (propriedade: PropriedadeCatalogo) => void;
  /** Propriedades que já viraram variável na mensagem — destacadas na lista. */
  variaveisUsadas?: string[];
  /** A mensagem vem de um template reutilizável: não se insere variável nela. */
  mensagemVinculada?: boolean;
};

/**
 * Pill de entidade — o seletor que o protótipo tinha e que escopa a lista de
 * variáveis. Diferente do "Entidades observadas" de lá, aqui a escolha não
 * muda o que a rotina consulta: as relações são declaradas pelo backend em
 * `relacoes` e não são escolha do usuário. Isto é navegação — "de qual
 * entidade eu quero esta variável".
 */
function EntidadePill({
  grupo,
  selecionado,
  onClick,
}: {
  grupo: GrupoEntidade;
  selecionado: boolean;
  onClick: () => void;
}) {
  const bloqueado = !grupo.selecionavel;
  return (
    <Tooltip
      label={bloqueado ? grupo.motivo ?? "Relação não disponível para seleção." : undefined}
      placement="top"
      openDelay={300}
      isDisabled={!bloqueado}
    >
      <Tag
        // preventDefault no mousedown evita que o clique tire o foco do campo
        // de mensagem antes do onClick disparar.
        onMouseDown={(e) => e.preventDefault()}
        onClick={bloqueado ? undefined : onClick}
        // Mesmo tamanho das pills de propriedade (PropriedadeList): as duas
        // listas ficam uma sobre a outra na sidebar, e tamanho diferente
        // fazia a de entidade parecer outra coisa.
        size="md"
        borderRadius="full"
        cursor={bloqueado ? "not-allowed" : "pointer"}
        opacity={bloqueado ? 0.5 : 1}
        {...(selecionado ? brandSolid("primary") : brandOutline("primary"))}
        {...fieldFont}
      >
        {bloqueado ? (
          <LockIcon boxSize={3} mr={1.5} />
        ) : (
          selecionado && <CheckIcon boxSize={3} mr={1.5} />
        )}
        <TagLabel>
          {grupo.rotulo}
          {grupo.selecionavel && ` (${grupo.quantidade})`}
        </TagLabel>
      </Tag>
    </Tooltip>
  );
}

/** Fileira "Entidade" que escopa a lista abaixo — a mesma no filtro e nas variáveis. */
function EntidadeSeletor({
  grupos,
  total,
  grupoValido,
  onChange,
}: {
  grupos: GrupoEntidade[];
  total: number;
  grupoValido: string | null;
  onChange: (grupo: string | null) => void;
}) {
  if (grupos.length <= 1) return null;
  return (
    <Box mb={2}>
      <Text fontSize="sm" fontWeight="700" color="primary" mb={1} px={1} {...fieldFont}>
        Entidade
      </Text>
      <Flex align="center" gap={2} bg="gray.50" borderRadius="md" p={2} wrap="wrap">
        <Tag
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onChange(null)}
          size="md"
          borderRadius="full"
          cursor="pointer"
          {...(grupoValido === null ? brandSolid("primary") : brandOutline("primary"))}
          {...fieldFont}
        >
          {grupoValido === null && <CheckIcon boxSize={3} mr={1.5} />}
          <TagLabel>Todas ({total})</TagLabel>
        </Tag>
        {grupos.map((grupo) => (
          <EntidadePill
            key={grupo.id}
            grupo={grupo}
            selecionado={grupoValido === grupo.id}
            onClick={() => onChange(grupo.id === grupoValido ? null : grupo.id)}
          />
        ))}
      </Flex>
    </Box>
  );
}

/**
 * Painel de escolha de campo — do filtro ou da variável de mensagem —, no
 * mesmo padrão visual do query builder embutido no formulário: rótulo
 * colorido + pills numa fileira neutra.
 *
 * As duas listas saem do mesmo catálogo (GET /catalogo/entidades), mas não
 * são iguais: filtrar exige que a propriedade declare operadores (hoje só
 * status e datas), enquanto o template aceita qualquer propriedade do contexto.
 * Por isso um campo pode aparecer nas variáveis e não no filtro.
 *
 * Cada trecho só aparece enquanto estiver sendo usado. Com nada ativo, a
 * sidebar fica só com a mensagem de estado — menos informação à toa.
 */
export default function VariaveisSidebar({
  contexto,
  catalogo,
  disparoAberto,
  semFiltro = false,
  hasActiveFiltroTarget,
  onSelectFiltroCampo,
  hasActiveTarget,
  onSelectVariavel,
  variaveisUsadas,
  mensagemVinculada,
}: Props) {
  const [busca, setBusca] = useState("");
  // null = todas as entidades. A escolha é por contexto: trocar de contexto
  // troca as relações, então o grupo escolhido deixa de existir.
  const [grupoAtivo, setGrupoAtivo] = useState<string | null>(null);
  // Separada da escolha das variáveis: o filtro e a mensagem são sessões
  // diferentes, e a entidade de uma não deve esconder campos da outra.
  const [grupoFiltroAtivo, setGrupoFiltroAtivo] = useState<string | null>(null);
  // Grupos recolhidos da lista. Fica aqui, e não na lista, porque a lista
  // desmonta a cada troca de foco entre campos e voltaria toda aberta.
  const [recolhidosFiltro, setRecolhidosFiltro] = useState<string[]>([]);
  const [recolhidosVariavel, setRecolhidosVariavel] = useState<string[]>([]);

  const termo = busca.trim().toLowerCase();
  const bate = (p: PropriedadeCatalogo) =>
    !termo || p.descricao.toLowerCase().includes(termo) || p.id.toLowerCase().includes(termo);

  // Evento não tem filtro: nem os campos nem a fileira de entidades aparecem,
  // mesmo que uma condição tenha ficado ativa antes da troca de gatilho.
  const mostrarFiltro = !!hasActiveFiltroTarget && disparoAberto && !semFiltro;
  const mostrarVariaveis = hasActiveTarget && !disparoAberto && !mensagemVinculada;

  // Os grupos saem do conjunto inteiro do contexto, não do resultado da
  // busca — senão a fileira de entidades dança a cada tecla.
  const todasFiltraveis = propriedadesFiltraveis(catalogo, contexto);
  const gruposFiltro = gruposDoContexto(catalogo, contexto, todasFiltraveis);
  const grupoFiltroValido =
    grupoFiltroAtivo && gruposFiltro.some((g) => g.id === grupoFiltroAtivo) ? grupoFiltroAtivo : null;
  const camposFiltro = todasFiltraveis.filter(bate);

  const todasVariaveis = propriedadesDeTemplate(catalogo, contexto);
  const grupos = gruposDoContexto(catalogo, contexto, todasVariaveis);
  const grupoValido = grupoAtivo && grupos.some((g) => g.id === grupoAtivo) ? grupoAtivo : null;

  const camposVariavel = todasVariaveis.filter(bate);

  // Escolher uma entidade não esconde as outras: o grupo dela vem para o topo,
  // aberto, e os demais ficam recolhidos na lista, a um clique. "Todas" abre tudo.
  const escolherEntidade = (
    grupo: string | null,
    todos: GrupoEntidade[],
    setGrupo: (g: string | null) => void,
    setRecolhidos: (r: string[]) => void,
  ) => {
    setGrupo(grupo);
    setRecolhidos(grupo ? todos.map((g) => g.id).filter((id) => id !== grupo) : []);
  };
  const relacoes = entidadeDoContexto(catalogo, contexto)?.relacoes;

  // Tela estreita (celular): a barra não cabe ao lado do formulário — com
  // 380px fixos ela ocupava a tela inteira e o formulário sumia. Ela desce
  // para baixo do formulário (ver DisparoScreen) e, enquanto se escolhe o
  // campo de um filtro, sobe como folha fixa no rodapé: a escolha é o passo
  // seguinte ao "+", e não pode exigir rolar a página até o fim.
  const compacto = useBreakpointValue({ base: true, lg: false }, { ssr: false }) ?? false;
  const folha = compacto && mostrarFiltro;

  const posicao = folha
    ? {
        position: "fixed" as const,
        left: 0,
        right: 0,
        bottom: 0,
        maxH: "55vh",
        zIndex: "overlay",
        borderTop: "1px solid",
        boxShadow: "0 -4px 16px rgba(0, 0, 0, 0.12)",
        borderTopRadius: "xl",
      }
    : compacto
      ? { w: "100%", maxH: "60vh", borderTop: "1px solid" }
      : {
          w: "380px",
          flexShrink: 0,
          borderLeft: "1px solid",
          position: "sticky" as const,
          top: 4,
          alignSelf: "stretch",
        };

  return (
    <Box
      as="aside"
      {...posicao}
      borderColor={cardStyles.borderColor}
      bg="#FAFBFC"
      display="flex"
      flexDirection="column"
    >
      <Flex align="center" gap={2} px={4} py={3} borderBottom="1px solid" borderColor={cardStyles.borderColor}>
        <Icon as={MdListAlt} boxSize={4} color="gray.500" />
        <Text fontSize="sm" fontWeight="700" color="gray.800" {...fieldFont} flex={1}>
          Campos do contexto
        </Text>
      </Flex>

      <Box px={4} py={3} borderBottom="1px solid" borderColor={cardStyles.borderColor}>
        {(mostrarFiltro || mostrarVariaveis) && (
          <InputGroup size="sm" mb={2}>
            <InputLeftElement pointerEvents="none">
              <Icon as={MdSearch} color="gray.400" boxSize={4} />
            </InputLeftElement>
            <Input
              placeholder="Buscar campo..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              bg="white"
              {...fieldFont}
              fontSize="sm"
            />
            {busca && (
              <IconButton
                aria-label="Limpar busca"
                icon={<CloseIcon boxSize={2.5} />}
                size="xs"
                variant="ghost"
                position="absolute"
                right={1}
                top="50%"
                transform="translateY(-50%)"
                onClick={() => setBusca("")}
              />
            )}
          </InputGroup>
        )}

        <Text fontSize="xs" color="gray.500" {...fieldFont}>
          {mostrarFiltro
            ? "Escolha a entidade e o campo do filtro."
            : mostrarVariaveis
              ? "Escolha a entidade e clique num campo pra inserir como variável."
              : !disparoAberto && mensagemVinculada
                ? "A mensagem vem de um template reutilizável — as variáveis são as dele. Para inserir variável, desfaça o vínculo em Origem da mensagem."
              : disparoAberto && !semFiltro
                ? "Clique no + do filtro, ou numa condição, pra ver os campos aqui."
                : "Clique num campo de mensagem pra ver as variáveis aqui."}
        </Text>
      </Box>

      <VStack align="stretch" spacing={0} flex={1} overflowY="auto" py={2} px={3}>
        {mostrarFiltro && (
          <Box mb={2}>
            <EntidadeSeletor
              grupos={gruposFiltro}
              total={todasFiltraveis.length}
              grupoValido={grupoFiltroValido}
              onChange={(g) => escolherEntidade(g, gruposFiltro, setGrupoFiltroAtivo, setRecolhidosFiltro)}
            />
            <Text fontSize="sm" fontWeight="700" color="secondary" mb={1} px={1} {...fieldFont}>
              Campo do filtro
            </Text>
            <Box bg="gray.50" borderRadius="md" p={2}>
              <PropriedadeList
                propriedades={camposFiltro}
                relacoes={relacoes}
                onSelect={(p) => onSelectFiltroCampo?.(p)}
                contexto={contexto}
                nomeContexto={contexto && rotuloContexto(contexto, catalogo)}
                recolhidos={recolhidosFiltro}
                onRecolhidosChange={setRecolhidosFiltro}
                forcarAberto={!!termo}
                destaque={grupoFiltroValido}
                vazio="Nenhum campo deste contexto aceita filtro."
              />
            </Box>
          </Box>
        )}

        {mostrarVariaveis && (
          <Box mb={2}>
            {/* "Todas" mantém o comportamento anterior, com os grupos um
                embaixo do outro. */}
            <EntidadeSeletor
              grupos={grupos}
              total={todasVariaveis.length}
              grupoValido={grupoValido}
              onChange={(g) => escolherEntidade(g, grupos, setGrupoAtivo, setRecolhidosVariavel)}
            />

            <Text fontSize="sm" fontWeight="700" color="secondary" mb={1} px={1} {...fieldFont}>
              Variáveis de mensagem
            </Text>
            <Box bg="gray.50" borderRadius="md" p={2}>
              <PropriedadeList
                propriedades={camposVariavel}
                relacoes={relacoes}
                onSelect={onSelectVariavel}
                usadas={variaveisUsadas}
                contexto={contexto}
                nomeContexto={contexto && rotuloContexto(contexto, catalogo)}
                recolhidos={recolhidosVariavel}
                onRecolhidosChange={setRecolhidosVariavel}
                forcarAberto={!!termo}
                destaque={grupoValido}
                vazio="Nenhum campo encontrado."
              />
            </Box>
          </Box>
        )}
      </VStack>
    </Box>
  );
}
